import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Spinner, Badge } from 'react-bootstrap';
import {
  useListB2BAnnouncementsQuery,
  useCreateB2BAnnouncementMutation,
  useUpdateB2BAnnouncementMutation,
  useDeleteB2BAnnouncementMutation,
} from '../../app/api';
import { brand, cardStyle, headStyle, EntPage } from './common';
import AnnouncementBody from '../../components/AnnouncementBody';
import AnnouncementBodyEditor from '../../components/AnnouncementBodyEditor';

/* What every B2B firm member sees on their portal dashboard.
 *
 * One list, the same model as HR announcements for employees, targeted only by
 * firm stage: live firms (the default), transitioning firms, or both. A
 * transitioning firm never sees an item written for live firms. Open to any ENTREPRENEUR staffer, again matching
 * HR announcements being open to any HR user rather than admins only.
 *
 * The event date is what makes an item a "Save the Date": set it and the item
 * files under that heading on the member dashboard AND stops showing the day
 * after the event, so nobody has to come back and delete last month's webinar.
 * Leave it blank for standing information. */

const emptyDraft = { title: '', body: '', event_date: '', pinned: false, expires_at: '', audience: 'active' };

const AUDIENCE = {
  active: 'Live firms',
  transitioning: 'Transitioning firms',
  all: 'All firms',
};

// Dates come back as YYYY-MM-DD (or an ISO datetime for expires_at); <input
// type="date"> wants exactly YYYY-MM-DD.
const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

const fmtDate = (iso) => {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y) return '';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short', month: 'long', day: 'numeric', year: 'numeric',
  });
};

const isPast = (iso) => toDateInput(iso) && toDateInput(iso) < new Date().toISOString().slice(0, 10);

const inputStyle = {
  width: '100%', padding: '6px 10px', border: `1px solid ${brand.softGray}`,
  borderRadius: 6, fontSize: '0.85rem', color: brand.deepBlue, outline: 'none', background: 'white',
};
const btnPrimary = {
  background: brand.deepBlue, color: 'white', border: 'none', borderRadius: 6,
  padding: '6px 14px', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
};
const btnPlain = {
  background: 'white', color: brand.charcoal, border: `1px solid ${brand.softGray}`,
  borderRadius: 6, padding: '6px 14px', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
};

function DraftFields({ draft, onChange }) {
  const set = (k, v) => onChange({ ...draft, [k]: v });
  return (
    <>
      <input style={{ ...inputStyle, marginBottom: '0.5rem' }} placeholder="Title"
        value={draft.title} onChange={(e) => set('title', e.target.value)} />
      <AnnouncementBodyEditor
        value={draft.body}
        onChange={(body) => set('body', body)}
        textareaStyle={{ ...inputStyle, minHeight: 80, resize: 'vertical', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
        accentColor={brand.tealBlue}
        hintColor={brand.charcoal}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.82rem' }}>
          Event date:
          <input type="date" style={{ ...inputStyle, width: 'auto', padding: '4px 8px' }}
            value={draft.event_date} onChange={(e) => set('event_date', e.target.value)} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.82rem' }}>
          Hide after:
          <input type="date" style={{ ...inputStyle, width: 'auto', padding: '4px 8px' }}
            value={draft.expires_at} onChange={(e) => set('expires_at', e.target.value)} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.82rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!draft.pinned} onChange={(e) => set('pinned', e.target.checked)} />
          Pin to top
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.82rem' }}>
          Show to:
          <select style={{ ...inputStyle, width: 'auto', padding: '4px 8px' }}
            value={draft.audience} onChange={(e) => set('audience', e.target.value)}>
            {Object.entries(AUDIENCE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>
      <div style={{ fontSize: '0.7rem', color: brand.charcoal, marginTop: '0.5rem' }}>
        With an event date this shows under <strong>Save the Date</strong> and disappears by itself
        the day after. Without one it shows under <strong>Important information</strong> and stays
        until it&apos;s hidden or deleted.
      </div>
    </>
  );
}

DraftFields.propTypes = {
  draft: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default function Announcements() {
  const { data = [], isLoading, refetch } = useListB2BAnnouncementsQuery();
  const [create, createState] = useCreateB2BAnnouncementMutation();
  const [update, updateState] = useUpdateB2BAnnouncementMutation();
  const [remove] = useDeleteB2BAnnouncementMutation();

  const [showNew, setShowNew] = useState(false);
  const [newDraft, setNewDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyDraft);
  const [error, setError] = useState(null);

  const saving = createState.isLoading || updateState.isLoading;

  const payload = (d) => ({
    title: d.title.trim(),
    body: d.body || '',
    event_date: d.event_date || null,
    pinned: !!d.pinned,
    expires_at: d.expires_at || null,
    audience: d.audience || 'active',
  });

  const submitNew = async () => {
    setError(null);
    try {
      await create(payload(newDraft)).unwrap();
      setNewDraft(emptyDraft);
      setShowNew(false);
      await refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Could not post that announcement.');
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setEditDraft({
      title: a.title || '',
      body: a.body || '',
      event_date: toDateInput(a.event_date),
      pinned: !!a.pinned,
      expires_at: toDateInput(a.expires_at),
      audience: a.audience || 'active',
    });
  };

  const submitEdit = async () => {
    setError(null);
    try {
      await update({ id: editingId, ...payload(editDraft) }).unwrap();
      setEditingId(null);
      await refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Could not save that announcement.');
    }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    setError(null);
    try {
      await remove({ id: a.id }).unwrap();
      if (editingId === a.id) setEditingId(null);
      await refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Could not delete that announcement.');
    }
  };

  return (
    <EntPage
      title="Announcements"
      subtitle="Shown on firm members' dashboards in the Entrepreneur portal - live firms, transitioning firms, or both."
      actions={
        <button type="button" style={btnPrimary} onClick={() => setShowNew((v) => !v)} disabled={saving}>
          <i className={`bi ${showNew ? 'bi-x' : 'bi-plus'} me-1`} />
          {showNew ? 'Cancel' : 'New announcement'}
        </button>
      }
    >
      {error && (
        <div style={{ background: 'rgba(192,57,43,0.08)', border: `1px solid ${brand.red}`, color: brand.red, borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
          <i className="bi bi-exclamation-triangle me-1" />{error}
        </div>
      )}

      {showNew && (
        <Card style={{ ...cardStyle, marginBottom: '1rem' }}>
          <Card.Header style={headStyle}>New announcement</Card.Header>
          <Card.Body>
            <DraftFields draft={newDraft} onChange={setNewDraft} />
            <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.5rem' }}>
              <button type="button" style={btnPrimary} onClick={submitNew} disabled={saving || !newDraft.title.trim()}>
                {saving ? 'Posting…' : 'Post'}
              </button>
              <button type="button" style={btnPlain} onClick={() => { setShowNew(false); setNewDraft(emptyDraft); }}>
                Cancel
              </button>
            </div>
          </Card.Body>
        </Card>
      )}

      <Card style={cardStyle}>
        <Card.Header style={{ ...headStyle, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span>Posted</span>
          <Badge bg="secondary" style={{ fontWeight: 600 }}>{data.length}</Badge>
        </Card.Header>
        <Card.Body>
          {isLoading && <Spinner size="sm" />}
          {!isLoading && data.length === 0 && (
            <div style={{ color: brand.charcoal, fontSize: '0.87rem' }}>
              Nothing posted yet. Firm dashboards will show a short placeholder until there is.
            </div>
          )}

          {data.map((a) => (
            <div key={a.id} style={{ borderBottom: `1px solid ${brand.softGray}`, paddingBottom: '1rem', marginBottom: '1rem' }}>
              {editingId === a.id ? (
                <>
                  <DraftFields draft={editDraft} onChange={setEditDraft} />
                  <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.5rem' }}>
                    <button type="button" style={btnPrimary} onClick={submitEdit} disabled={saving || !editDraft.title.trim()}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button type="button" style={btnPlain} onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, color: brand.deepBlue }}>{a.title}</span>
                    {a.pinned && <Badge bg="warning" text="dark" style={{ fontWeight: 600 }}>Pinned</Badge>}
                    {a.audience && a.audience !== 'active' && (
                      <Badge bg="light" text="dark" style={{ fontWeight: 600, border: `1px solid ${brand.softGray}` }}>{AUDIENCE[a.audience]}</Badge>
                    )}
                    {a.event_date && (
                      // Past events are still listed here — staff need to see what
                      // aged out — but marked, because members no longer see them.
                      <Badge bg={isPast(a.event_date) ? 'secondary' : 'info'} style={{ fontWeight: 600 }}>
                        <i className="bi bi-calendar-event me-1" />
                        {fmtDate(a.event_date)}{isPast(a.event_date) ? ' — past' : ''}
                      </Badge>
                    )}
                  </div>
                  {a.body && (
                    <AnnouncementBody
                      style={{ color: brand.charcoal, fontSize: '0.85rem', marginTop: '0.35rem' }}
                      linkColor={brand.tealBlue}
                    >
                      {a.body}
                    </AnnouncementBody>
                  )}
                  <div style={{ color: brand.charcoal, fontSize: '0.72rem', marginTop: '0.4rem' }}>
                    {a.created_by ? `Posted by ${a.created_by}` : 'Posted'}
                    {a.expires_at ? ` · hidden after ${fmtDate(a.expires_at)}` : ''}
                  </div>
                  <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                    <button type="button" style={btnPlain} onClick={() => startEdit(a)}>Edit</button>
                    <button type="button" style={{ ...btnPlain, color: brand.red, borderColor: brand.red }} onClick={() => del(a)}>
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </Card.Body>
      </Card>
    </EntPage>
  );
}
